/**
 * <PasswordInput> — password input with show/hide toggle and live policy hints.
 *
 * The policy hints mirror the bc_shared.auth.validate_password_strength rules:
 *   - 7+ characters
 *   - 1 uppercase
 *   - 1 lowercase
 *   - 1 digit
 *   - 1 special character
 *
 * Hints update as the user types so they know exactly what's missing without
 * waiting for a server-side validation round trip. The actual validation
 * still happens server-side (defence in depth) — these hints are UX, not
 * security.
 *
 * Color-agnostic — pass `className` to restyle the input. The hint icons
 * use neutral grey + green / red defaults; override via the `hintClassName`
 * prop if your brand demands different colors.
 */

import React, { useState, forwardRef } from 'react';

const RULES = [
  { test: (v) => v.length >= 7, label: 'At least 7 characters' },
  { test: (v) => /[A-Z]/.test(v), label: 'One uppercase letter' },
  { test: (v) => /[a-z]/.test(v), label: 'One lowercase letter' },
  { test: (v) => /\d/.test(v), label: 'One digit' },
  { test: (v) => /[^a-zA-Z0-9]/.test(v), label: 'One special character' },
];

/**
 * @typedef {object} PasswordInputProps
 * @property {string} value
 * @property {(value: string) => void} onChange
 * @property {string} [id]
 * @property {string} [name]
 * @property {string} [placeholder]
 * @property {boolean} [showPolicy]      — Render the live policy hint list. Default true.
 * @property {boolean} [autoFocus]
 * @property {string} [className]
 * @property {string} [hintClassName]
 * @property {string} [autoComplete]
 */

export const PasswordInput = forwardRef(function PasswordInput(
  {
    value,
    onChange,
    id,
    name = 'password',
    placeholder = 'Password',
    showPolicy = true,
    autoFocus = false,
    className = '',
    hintClassName = '',
    autoComplete = 'new-password',
    ...rest
  },
  ref,
) {
  const [reveal, setReveal] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <input
          ref={ref}
          id={id}
          name={name}
          type={reveal ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          className={`w-full min-h-touch px-3 pr-12 py-2 border border-slate-300 rounded-md text-base ${className}`}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setReveal((r) => !r)}
          // The button needs a real click target on mobile but should not
          // visually push the field off-centre — absolute positioning with
          // a 44px hit area inflated by min-h/w solves both.
          className="absolute right-1 top-1/2 -translate-y-1/2 min-h-touch min-w-touch text-xs text-slate-500 hover:text-slate-700 px-2"
          aria-label={reveal ? 'Hide password' : 'Show password'}
        >
          {reveal ? 'Hide' : 'Show'}
        </button>
      </div>
      {showPolicy && (
        <ul className={`text-xs space-y-1 ${hintClassName}`}>
          {RULES.map((rule) => {
            const passed = rule.test(value);
            return (
              <li
                key={rule.label}
                className={passed ? 'text-green-700' : 'text-slate-500'}
              >
                <span aria-hidden="true">{passed ? '✓ ' : '○ '}</span>
                {rule.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
});
